/**
 * Welcome page settings tab - the org-admin entry point to the tenant's
 * consumer landing page (the page served at `/welcome/<slug>` by the consumer
 * app).
 *
 * It hosts the shared `WelcomePageEditor` that the retired staff tenant view
 * used, writing the same record (`Tenant.settings["welcome_page"]`) through the
 * org-scoped endpoint. Reserved for admins/superadmins: the route is wrapped in
 * `<ProtectedRoute adminOnly>` and the sidebar entry only renders for
 * `usePermissions().isAdmin`; the backend endpoint enforces the same via
 * `IsOrgAdmin`.
 *
 * The `enabled` switch at the top is the master on/off for the whole page: when
 * off, the editor is hidden and (once saved) the public page is taken offline.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import { WelcomePageEditor } from '@/admin/components/welcome-page-editor'
import {
  EMPTY_WELCOME_PAGE,
  normaliseWelcomePage,
  type WelcomePageState,
} from '@/admin/components/welcome-page-editor-utils'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import { toastApiError } from '@/shared/lib/api-error'

interface WelcomePageResponse {
  welcome_page: Partial<WelcomePageState> | null
  welcome_url: string | null
}

export function WelcomePageTab() {
  const { t } = useTranslation()
  const { orgUuid, orgName } = useOrg()
  const queryClient = useQueryClient()

  const { data, isPending, isError } = useQuery<WelcomePageResponse>({
    queryKey: ['org-welcome-page', orgUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/welcome-page/`)
        .then((res) => res.data as WelcomePageResponse),
    enabled: Boolean(orgUuid),
  })

  // The editable draft. Persistence is explicit (Save), like the theme editor.
  // The draft tracks the server value whenever it changes UNLESS the user has
  // unsaved edits (`dirty`): so a background refetch can't clobber in-progress
  // edits, but a stale cache-hydrated value IS corrected once the refetch
  // settles. `org-welcome-page` is a persisted DETAIL query with
  // `refetchOnMount: 'always'`, so on reload the toggle first paints from a
  // possibly-stale IndexedDB value and then the refetch arrives - a plain
  // "seed once" gate would freeze that stale value in the draft forever.
  const [page, setPage] = useState<WelcomePageState>(EMPTY_WELCOME_PAGE)
  const [hasSeeded, setHasSeeded] = useState(false)
  const [dirty, setDirty] = useState(false)
  // Any user edit marks the draft dirty so an in-flight refetch won't overwrite
  // it; Save clears it, making the saved value the new clean baseline.
  const editPage = (next: WelcomePageState) => {
    setPage(next)
    setDirty(true)
  }
  useEffect(() => {
    // Clear the previous org's draft immediately on switch so it can never be
    // shown or saved against the new org before its data seeds.
    setPage(EMPTY_WELCOME_PAGE)
    setHasSeeded(false)
    setDirty(false)
  }, [orgUuid])
  useEffect(() => {
    if (data && !dirty) {
      setPage(normaliseWelcomePage(data.welcome_page))
      setHasSeeded(true)
    }
  }, [data, dirty])

  const welcomeUrl = data?.welcome_url ?? null

  const save = useMutation({
    mutationFn: (welcome_page: WelcomePageState) =>
      api
        .put(`/organisations/${orgUuid}/welcome-page/`, { welcome_page })
        .then((res) => res.data as WelcomePageResponse),
    onSuccess: (resp) => {
      toast.success(t('welcomePage.saved', { defaultValue: 'Welcome page saved' }))
      // The PUT response is the new authoritative state; write it into the
      // cache synchronously and only mark stale for future mounts. The saved
      // value is now the clean baseline, so clear `dirty` - a later refetch may
      // safely re-sync the draft from the server.
      queryClient.setQueryData(['org-welcome-page', orgUuid], resp)
      queryClient.invalidateQueries({
        queryKey: ['org-welcome-page', orgUuid],
        refetchType: 'none',
      })
      setDirty(false)
    },
    onError: (err) =>
      toastApiError(
        err,
        t('welcomePage.saveFailed', { defaultValue: 'Failed to save welcome page' }),
      ),
  })

  if (isError) {
    return (
      <p className="py-12 text-center text-[13px] text-[var(--ink-3)]">
        {t('welcomePage.loadError', {
          defaultValue:
            'Welcome page settings could not be loaded. Refresh and try again.',
        })}
      </p>
    )
  }

  // Wait for the draft to be seeded from the server, not just for the query to
  // resolve. `page` starts as EMPTY_WELCOME_PAGE and is seeded in an effect that
  // runs AFTER the first non-pending render, so rendering on `!isPending` alone
  // briefly shows the toggle in its default (disabled) state before the effect
  // flips it to the persisted value - a visible flicker, and a race where a fast
  // toggle can be clobbered by the late seed. Gating on `hasSeeded` too means the
  // controls only ever render already reflecting the saved state. (isError is
  // handled above so this can't loader-spin on a failed load.)
  if (isPending || !hasSeeded) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="clara-heading">
            {t('welcomePage.title', { defaultValue: 'Welcome page' })}
          </h2>
          <p className="mt-1 max-w-xl text-[13px] text-[var(--ink-3)]">
            {t('welcomePage.description', {
              defaultValue:
                'The public landing page shown to customers before they register. Turn it off to take it offline; turn it on to author its content.',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {page.enabled && welcomeUrl && (
            <a href={welcomeUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm">
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                {t('welcomePage.viewLive', { defaultValue: 'View live' })}
              </Button>
            </a>
          )}
          <Button
            size="sm"
            onClick={() => save.mutate(page)}
            disabled={save.isPending}
            data-testid="welcome-page-save-btn"
          >
            {save.isPending
              ? t('welcomePage.saving', { defaultValue: 'Saving...' })
              : t('welcomePage.saveChanges', { defaultValue: 'Save changes' })}
          </Button>
        </div>
      </div>

      {/* ── Master enable/disable toggle ── */}
      <section className="flex items-center justify-between rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5">
        <div>
          <Label
            htmlFor="welcome-page-enabled"
            className="text-[14px] text-[var(--ink)]"
          >
            {t('welcomePage.enabledLabel', { defaultValue: 'Welcome page enabled' })}
          </Label>
          <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">
            {page.enabled
              ? t('welcomePage.liveStatus', {
                  defaultValue: 'Your welcome page is live for customers.',
                })
              : t('welcomePage.disabledStatus', {
                  defaultValue:
                    'Your welcome page is turned off and hidden from customers.',
                })}
          </p>
        </div>
        <Switch
          id="welcome-page-enabled"
          checked={page.enabled}
          onCheckedChange={(v) => editPage({ ...page, enabled: v })}
          data-testid="welcome-page-enabled-toggle"
        />
      </section>

      {/* ── Editor (hidden while disabled) ── */}
      {page.enabled ? (
        <WelcomePageEditor
          page={page}
          onChange={editPage}
          orgName={orgName ?? ''}
        />
      ) : (
        <p className="text-[13px] text-[var(--ink-3)]">
          {t('welcomePage.turnOnPrompt', {
            defaultValue:
              'Turn the welcome page on above to configure its sections and content.',
          })}
        </p>
      )}
    </div>
  )
}
